import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, OperatorFunction, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { OrderHttpService } from '../http/order.http.service';
import { SalesDocumentHttpService } from '../http/sales-document.http.service';
import { OrderAliasApiModel } from '../model/api-model/order/order-alias.api.model';
import { CanBeRefundedRequest } from '../model/request/order-refund.request.models';
import { RefundTransactionRequestModel } from '../model/request/refund-transaction-payment.request.model';
import { RefundTransactionResponseModel } from '../model/response/refund-transaction.response.model';
import { CanBeRefundViewModel } from '../model/view-model/sales-document/can-be-refund.view.model';
import { SalesDocumentPaymentViewModel } from '../model/view-model/sales-document/salesdocument-payment.view.model';
import { RefundViewModel } from '../model/view-model/sales-document/refund.view.model';
import { CanBeRefundedResponse } from '../model/response/order-refund.response.models';
import { TranslateService } from '@ngx-translate/core';
import { TransactionNumberByVoucherNumber } from '../model/interface/transaction-number-by-voucher-number.interface';
import { CustomError, ErrorHandlerService } from '../service/error-handler/error-handler.service';

@Injectable({
  providedIn: 'root'
})
export class RefundDataProvider {
  constructor(
    private salesDocumentHttpService: SalesDocumentHttpService,
    private orderHttpService: OrderHttpService,
    private translateService: TranslateService,
    private errorHandlerService: ErrorHandlerService
  ) {
    if (this.salesDocumentHttpService && typeof this.salesDocumentHttpService.setNoUserContext === 'function') {
      this.salesDocumentHttpService.setNoUserContext(true);
    }
  }

  public canBeRefunded(cinemaId: string, request: CanBeRefundedRequest): Observable<CanBeRefundedResponse> {
    return this.salesDocumentHttpService.canBeRefunded(cinemaId, request).pipe(this.applyRefundValidation());
  }

  public canBeRefundedViaApiModel(cinemaId: string, request: CanBeRefundedRequest): Observable<CanBeRefundViewModel> {
    return this.salesDocumentHttpService.canBeRefundedViaApiModel(cinemaId, request).pipe(map((x) => new CanBeRefundViewModel(x)));
  }

  public getSalesDocument(cinemaId: string, salesDocumentId: string, request: CanBeRefundedRequest) {
    return forkJoin([
      this.salesDocumentHttpService.getSalesDocumentPayment(cinemaId, salesDocumentId),
      this.salesDocumentHttpService.getSalesDocumentById(cinemaId, salesDocumentId),
      this.canBeRefunded(cinemaId, request)
    ]).pipe(
      map(([refundTransactionPaymentResponse, salesDocumentResponseModel, canBeRefundedResponse]) => {
        return { refundTransactionPaymentResponse, salesDocumentResponseModel, canBeRefundedResponse };
      })
    );
  }

  public refund(cinemaId: string, salesDocumentId: string, request: RefundTransactionRequestModel): Observable<RefundTransactionResponseModel> {
    return this.salesDocumentHttpService.refund(cinemaId, salesDocumentId, request);
  }

  public postSalesDocumentRefund(cinemaId: string, salesDocumentId: string, request: RefundTransactionRequestModel): Observable<RefundViewModel> {
    return this.salesDocumentHttpService.postSalesDocumentRefund(cinemaId, salesDocumentId, request).pipe(map((x) => new RefundViewModel(x)));
  }

  public getSalesDocumentPaymentViaApiModel(cinemaId: string, salesDocumentId: string): Observable<SalesDocumentPaymentViewModel[]> {
    return this.salesDocumentHttpService
      .getSalesDocumentPaymentViaApiModel(cinemaId, salesDocumentId)
      .pipe(map((x) => x.map((y) => new SalesDocumentPaymentViewModel(y))));
  }

  public getOrderTransactionNumber(bookingId: string): Observable<OrderAliasApiModel> {
    return this.orderHttpService.getOrderTransactionNumber(bookingId);
  }

  public getSalesDocumentById(cinemaId: string, salesDocumentId: string) {
    return this.salesDocumentHttpService.getSalesDocumentById(cinemaId, salesDocumentId);
  }

  public getTransactionNumberByVoucherNumber(voucherNumber: string): Observable<TransactionNumberByVoucherNumber> {
    return this.orderHttpService.getTransactionNumberByVoucherNumber(voucherNumber);
  }

  private applyRefundValidation(): OperatorFunction<CanBeRefundedResponse, CanBeRefundedResponse> {
    return (source: Observable<CanBeRefundedResponse>) =>
      source.pipe(
        map((response) => {
          const { refundToken, executionStatus } = response;

          if (!refundToken && executionStatus > 0) {
            const code = executionStatus.toString();
            const { message } = this.getRefundTranslation(code, true);
            throw new CustomError(message, code);
          }

          return response;
        }),
        catchError((err) => {
          if (err instanceof CustomError) {
            return throwError(() => err);
          }

          const errorData = this.errorHandlerService.getError(err);
          if (errorData) {
            const { message, translated } = this.getRefundTranslation(errorData.code, false);
            if (translated) {
              return throwError(() => new CustomError(message, errorData.code, errorData.status));
            }
          }

          return throwError(() => err);
        })
      );
  }

  private getRefundTranslation(code: string, useDefaultFallback: boolean): { message: string; translated: boolean } {
    const translationKey = `refund.error.${code}`;
    const specificMessage = this.translateService.instant(translationKey);
    const isTranslated = specificMessage !== translationKey;

    let message = specificMessage;

    if (!isTranslated && useDefaultFallback) {
      message = this.translateService.instant('refund.error.default');
    }

    return { message, translated: isTranslated };
  }
}
